import { useRef, useState } from "react";
import { DOSYA_GIRDI_TUR_ADI, PROJE_TANIMA_ADI, projeAralikMetni, projeOkumaEtiketi, projeOkumaGelenTuruMu, projeGirdiTuru, projeSayfaMetniOku, type GelenHedef, type ProjeOkumaKarti, type ProjeTanima } from "@oasis/domain";
import { Band, Buton, Etiket } from "@/components/parcalar";
import { AltBaslik, Nokta, UyariSatiri } from "@/components/belge-okuma-bolumu";
import { api } from "@/lib/api";
import {
  OKUMA_UCU,
  PROJE_UCU,
  okumaDugmesi,
  okumaEylemleri,
  projeBolumMetni,
  projeDegerMetni,
  projeFormu,
  projeIkinciMetni,
  projeKarsilastirmaSatirlari,
  projeSayfaEngeli,
  projeYerMetni,
} from "@/lib/belgeler";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { dosyaYukle, tarihSaat } from "@/lib/veri";

/**
 * D-074 Faz 5 · PR 26: Documents kartının proje belgesi "Document reading" bölümü (prototip belgeler--proje-okuma, kart 1-4; `BoyaOkumaBolumu` iskeleti). Okuma yalnız yerin, kuralların ve sayfa
 * önerisinin kullandığı değerleri gösterir (sözleşme §17): Kind, riser no, hat SN'leri, belge no ve rev, imza ve "For DNV review" doluluğu, sonraki sörvey; datapack'te bölümler (Parts of the
 * datapack), DNV alt parça SN'leri ve istisna sayfalar (Page marks). Sayfa aralığı ve riser yalnız önerilir: "Use the reading" kartın bugünkü tür+yer kaydını çağırır (`yerKaydet`, POST
 * :id/tur) ve Pages'i doldurur; hiçbir iş kaydı yazılmaz, kural kaynağı yazılmaz. Başka riserin sayfaları (PB-01) o risere taşınmaz. Dosyalanmış datapack'te "Also file as {type}" aynı PDF'i
 * öteki türle bugünkü Project documents bağ yazımından dosyalar; sayfaları kişi onaylar. `salt` kapanmış satır içindir. Okuma excel'inin adı kartta yoktur.
 */

const ETIKET_SINIFI = "text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3";
const TABLO_BASI = "px-2 py-1.5 text-left text-xs font-medium text-ink-3";
const TABLO_HUCRESI = "px-2 py-1.5 align-top [overflow-wrap:anywhere]";

export interface ProjeFormYardimi {
  /** "Use the reading": dosyalama formunun yeri (riser) */
  kullan: (h: GelenHedef) => void;
  /** "Use the reading": Pages alanı */
  sayfalar: (s: string) => void;
}

/** Okunmayanlar cümlesi (Kind'e göre; sözleşme §17.3 "Okunmayanlar"). */
function okunmayanlarMetni(tanima: ProjeTanima): string {
  switch (tanima) {
    case "datapack": return cevir("Not read: the value tables inside the datapack, MWPs and maintenance dates on the CoC, the product text and result text of the DNV report, and every other page value by value (only the riser number and line SN marks are looked for).");
    case "coc": return cevir("Not read: the item lines, part numbers, MWPs, maintenance and signature dates.");
    case "dnv survey": return cevir("Not read: the product text, result text and report date.");
    case "previous cycle": return cevir("Only the riser number of the previous cycle is read; nothing is written to today's records.");
    case "dvr": case "itp": case "procedure": case "ga drawing": return cevir("Only the cover is read: number and revision. The date, the approver, the body and the test pressures are not read (the hydrotest pressure rule waits for a decision).");
    default: return "";
  }
}

export function ProjeOkumaBolumu({ satirId, k, onYenile, salt, hedef, form, satirTuru }: {
  satirId: string; k: ProjeOkumaKarti; onYenile: () => void; salt?: boolean; hedef?: GelenHedef | null; form?: ProjeFormYardimi; satirTuru?: string | null;
}) {
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [degismedi, setDegismedi] = useState(false);
  const [ikinciSayfa, setIkinciSayfa] = useState<string>(k.ikinci?.sayfalar ? projeAralikMetni(k.ikinci.sayfalar) : "");
  const secici = useRef<HTMLInputElement>(null);
  const o = k.okuma;
  const ey = okumaEylemleri(k, salt);
  const yardim = salt ? undefined : form;

  const calistir = async (anahtar: string, f: () => Promise<unknown>) => {
    setMesgul(anahtar);
    setHata(null);
    setDegismedi(false);
    try {
      await f();
      onYenile();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(null);
    }
  };

  const sayfaLinki = (sayfa: number | null) =>
    o && sayfa !== null ? <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa })} target="_blank" rel="noreferrer">p.{sayfa}</a></> : null;
  const karsilastirmaSatirlari = projeKarsilastirmaSatirlari(k);
  const tumUyarilar = k.karsilastirma?.uyarilar ?? [];
  const kirmiziVar = tumUyarilar.some((u) => u.renk === "kirmizi" && !u.karar);
  const sariGri = tumUyarilar.length > 0 && tumUyarilar.every((u) => u.renk !== "kirmizi");
  const datapack = o?.tanima === "datapack";
  const formu = projeFormu(k, satirTuru ?? null);
  const yerRn = k.yer?.yer?.tur === "proje" && k.yer.yer.kapsam === "riser" ? k.yer.yer.anahtar : null;
  const gen11 = tumUyarilar.find((u) => u.kod === "GEN-11");
  const ikinci = k.ikinci;
  const ikinciAd = ikinci?.tur ? cevir(DOSYA_GIRDI_TUR_ADI[ikinci.tur]) : "";
  const ikinciAralik = projeSayfaMetniOku(ikinciSayfa);
  const ikinciEngeli = ikinci ? projeSayfaEngeli(k, ikinciSayfa, o?.sayfaSayisi ?? null, satirTuru ?? null) : null;

  return (
    <div data-bolum="proje-okuma">
      <h3 className={cn("mb-1.5", ETIKET_SINIFI)}>{cevir("Document reading")}</h3>
      {k.is.durum === "sirada" && <p className="mt-1 text-sm">{cevir("In the reading queue · place {0}", k.is.siraNo ?? "—")}</p>}
      {k.is.durum === "donusturuluyor" && <p className="mt-1 text-sm">{cevir("The converter is reading this file.")}</p>}
      {k.is.durum === "hata" && (
        <Band tip="not" className="mt-1.5">
          {cevir("Could not read: {0}", cevir(k.is.hata ?? ""))}
          {k.is.hataTs && <span className="mt-0.5 block text-xs">{tarihSaat(k.is.hataTs)}</span>}
        </Band>
      )}
      {!o && !k.is.durum && !salt && (
        <p className="mt-1 text-sm text-ink-2">{cevir("OASIS reads the project document for you to check: what it is, the riser, the line SNs, document number and revision, and in a datapack the pages of this riser's CoC and DNV survey report. Nothing is filed and no rule value is written.")}</p>
      )}
      {k.engel && !salt && <Band tip="not" className="mt-1.5">{cevir(k.engel)}</Band>}

      {o && (
        <>
          <p className="text-xs text-ink-3">
            {[
              k.kod ? cevir("{0} · reading job {1}", cevir(PROJE_TANIMA_ADI[o.tanima]), k.kod) : cevir(PROJE_TANIMA_ADI[o.tanima]),
              datapack ? cevir("{0} parts · {1} page marks · {2} DNV sub-assembly SNs", o.bolumSayisi, o.isaretSayisi, o.parcaSnSayisi) : null,
            ].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-ink-3">
            {o.kaynak.tur === "donusturucu"
              ? cevir("Read by the converter on {0} · engine {1}", tarihSaat(o.kaynak.ts), o.motor)
              : cevir("Reading excel uploaded by {0} on {1} · engine {2}", o.kaynak.ad, tarihSaat(o.kaynak.ts), o.motor)}
          </p>

          <ul className="mt-2 space-y-0.5 text-sm" data-bolum="proje-baslik">
            {k.degerler.map((d) => (
              <li key={d.alan} className="flex gap-2">
                <Nokta k={d.kesinlik} />
                <span className="w-[10rem] shrink-0 text-ink-2">{cevir(projeOkumaEtiketi(d.alan))}</span>
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  {d.deger ? projeDegerMetni(d.alan, d) : <span className="text-ink-3">{cevir("not read")}</span>}
                  {sayfaLinki(d.sayfa)}
                </span>
              </li>
            ))}
          </ul>

          {datapack && k.bolumler.length > 0 && (
            <div data-bolum="proje-bolumler">
              <AltBaslik>{cevir("Parts of the datapack")}</AltBaslik>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" data-bolum="bl-kars">
                  <thead><tr className="border-b border-line">
                    <th className={TABLO_BASI}>{cevir("Pages")}</th>
                    <th className={TABLO_BASI}>{cevir("Part")}</th>
                    <th className={TABLO_BASI}>{cevir("Riser on the cover")}</th>
                    <th className={TABLO_BASI}>{cevir("Result")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    {k.bolumler.map((b) => {
                      const m = projeBolumMetni(b, yerRn);
                      return (
                        <tr key={b.sira} data-bolum-sira={b.sira}>
                          <td className={TABLO_HUCRESI}>{m.sayfalar}{sayfaLinki(b.ilk)}</td>
                          <td className={TABLO_HUCRESI}>{m.belge}{b.rapor && <span className="block text-xs text-ink-3">{b.rapor.no}{b.rapor.rev ? ` Rev.${b.rapor.rev}` : ""}</span>}</td>
                          <td className={TABLO_HUCRESI}>{m.kapak}</td>
                          <td className={TABLO_HUCRESI}>{m.sonuc}{m.ek && <span className="block text-xs text-ink-3">{m.ek}</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="mt-1 text-xs text-ink-3">{cevir("A part is checked on its own cover page. Pages of another riser are never moved to that riser: exclude them, reference that riser's own report or reject the datapack.")}</p>
            </div>
          )}

          {k.parcaSnleri.length > 0 && (
            <div data-bolum="proje-parcalar">
              <AltBaslik>{cevir("DNV sub-assembly SNs")}</AltBaslik>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" data-bolum="bl-kars">
                  <thead><tr className="border-b border-line">
                    <th className={TABLO_BASI}>{cevir("Line")}</th>
                    <th className={TABLO_BASI}>{cevir("On the report")}</th>
                    <th className={TABLO_BASI}>OASIS</th>
                    <th className={TABLO_BASI}>{cevir("Result")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    {k.parcaSnleri.map((s) => {
                      const v = (a: string) => s.degerler.find((d) => d.alan === a);
                      return (
                        <tr key={s.no} data-parca={s.no}>
                          <td className={TABLO_HUCRESI}>{v("hat")?.deger ? cevir(v("hat")!.deger!) : "—"}</td>
                          <td className={TABLO_HUCRESI}><Nokta k={v("sn")?.kesinlik ?? "bos"} /> {v("sn")?.deger ?? "—"}{sayfaLinki(v("sn")?.sayfa ?? null)}</td>
                          <td className={TABLO_HUCRESI}>{s.oasis ?? "—"}</td>
                          <td className={TABLO_HUCRESI}>{s.sonuc === "ayni" ? cevir("same") : s.sonuc === "farkli" || s.sonuc === "uyari" ? cevir("differs") : cevir("not compared")}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {datapack && k.isaretler.length > 0 && (
            <div data-bolum="proje-isaretler">
              <AltBaslik>{cevir("Page marks")}</AltBaslik>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" data-bolum="bl-kars">
                  <thead><tr className="border-b border-line">
                    <th className={TABLO_BASI}>{cevir("Page")}</th>
                    <th className={TABLO_BASI}>{cevir("On the page")}</th>
                    <th className={TABLO_BASI}>{cevir("Result")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    {k.isaretler.map((s) => {
                      const v = (a: string) => s.degerler.find((d) => d.alan === a)?.deger ?? null;
                      const sayfa = s.degerler.find((d) => d.deger && d.sayfa !== null)?.sayfa ?? null;
                      return (
                        <tr key={s.no} data-isaret={s.no}>
                          <td className={TABLO_HUCRESI}>{sayfa ?? "—"}{sayfaLinki(sayfa)}</td>
                          <td className={TABLO_HUCRESI}>{[v("isaret_riser") ? `${cevir("Riser")} ${v("isaret_riser")}` : null, v("isaret_sn") ? `${v("isaret_hat") ? cevir(v("isaret_hat")!) : "SN"} ${v("isaret_sn")}` : null].filter(Boolean).join(" · ")}</td>
                          <td className={TABLO_HUCRESI}>{s.uyarilar.length ? s.uyarilar.map((u) => u.kod).join(", ") : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="mt-1 text-xs text-ink-3">{cevir("Only pages whose riser number or line SN differs from the cover are listed; the other pages are not read value by value.")}</p>
            </div>
          )}

          {k.yer && k.yer.durum !== "proje" && (
            <div data-bolum="proje-yer">
              <AltBaslik>{cevir("Where it goes")}</AltBaslik>
              <p className="text-sm">
                {projeYerMetni(k)}
                {k.yer.durum === "kisi" && <Etiket tone="ok" className="ml-1.5">{cevir("your choice")}</Etiket>}
                {k.yer.durum === "oneri" && <Etiket tone="info" className="ml-1.5">{cevir("proposed")}</Etiket>}
              </p>
              {datapack && (
                <ul className="mt-1 space-y-0.5 text-sm">
                  <li>{cevir("OSI Certificate of Conformance (1.1)")} · {k.sayfaOnerisi.coc ? cevir("pages {0}–{1}", k.sayfaOnerisi.coc.bas, k.sayfaOnerisi.coc.son) : cevir("No CoC of this riser found in the datapack.")}</li>
                  <li>{cevir("DNV Survey Report (2.2)")} · {k.sayfaOnerisi.dnv ? cevir("pages {0}–{1}", k.sayfaOnerisi.dnv.bas, k.sayfaOnerisi.dnv.son) : cevir("No DNV survey report of this riser found in the datapack.")}</li>
                </ul>
              )}
              {datapack && <p className="mt-1 text-xs text-ink-3">{cevir("Page ranges are suggestions only. You check them in the viewer and confirm.")}</p>}
            </div>
          )}

          {yardim && (formu.rn || formu.sayfalar) && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-bolum="okumayi-kullan">
              <Buton kucuk disabled={mesgul !== null} onClick={() => {
                const tur = hedef?.tur === "proje" ? hedef.belgeTuru : projeOkumaGelenTuruMu(satirTuru) ? projeGirdiTuru(satirTuru) : null;
                if (formu.rn && tur) yardim.kullan({ tur: "proje", kapsam: "riser", anahtar: formu.rn, belgeTuru: tur });
                if (formu.sayfalar) yardim.sayfalar(formu.sayfalar);
              }}>{cevir("Use the reading")}</Buton>
              <span className="w-full text-xs text-ink-3">
                {cevir("“Use the reading” fills Riser with the riser on the document and Pages with this riser’s pages for this type. You check both; nothing is filed until you confirm.")}
              </span>
            </div>
          )}
          {yardim && (kirmiziVar || datapack || sariGri) && (
            <Band tip="not" className="mt-2">
              <span className="block font-medium">{cevir("Before you file")}</span>
              {kirmiziVar && <span className="block">{cevir("Red alerts wait for a decision by the Quality Lead or the PM. Filing opens after the decision; File as reference and Reject stay open.")}</span>}
              {datapack && yerRn && <span className="block">{cevir("A datapack is one signed delivery of {0}: its pages are never moved to another riser.", yerRn)}</span>}
              {datapack && yerRn && k.yabanci.length > 0 && <span className="block">{cevir("Pages that are another riser's report (PB-01) are never filed for {0}: the pages you file must not include them.", yerRn)}</span>}
              {sariGri && <span className="block">{cevir("Amber and grey alerts do not stop filing; they stay with the reading.")}</span>}
            </Band>
          )}
          {gen11 && (
            <p className="mt-1 text-xs text-ink-3">{cevir("OASIS does not mark the old one: if this one replaces it, remove {0} on Project documents yourself.", gen11.degerler[0] ?? "—")}</p>
          )}

          {salt && ikinci && ikinci.durum !== "yok" && ikinci.tur && (
            <div data-bolum="proje-ikinci">
              <AltBaslik>{cevir("Also file as {0}", ikinciAd)}</AltBaslik>
              {ikinci.durum === "eklendi"
                ? <p className="text-sm">{cevir("Also filed as {0} by {1} on {2} · pages {3}", ikinciAd, ikinci.kim ?? "—", ikinci.ts ? tarihSaat(ikinci.ts) : "—", ikinci.eklenenSayfalar ? projeAralikMetni(ikinci.eklenenSayfalar) : "—")}</p>
                : <p className="text-sm">{projeIkinciMetni(ikinci.durum, ikinci.tur, ikinci.rn, ikinci.engel)}</p>}
              {ikinci.durum === "eklenebilir" && k.eylemler.ikinci && (
                <>
                  {ikinci.sayfalar && <p className="mt-1 text-xs text-ink-3">{cevir("Suggested from the reading: pages {0}–{1}.", ikinci.sayfalar.bas, ikinci.sayfalar.son)}</p>}
                  <label className="mt-1.5 flex items-center gap-2 text-sm">
                    <span className="text-ink-2">{cevir("Pages")}</span>
                    <input className="w-28 rounded-md border border-line px-2 py-1 text-sm" value={ikinciSayfa} disabled={mesgul !== null} aria-label={cevir("Pages")} onChange={(e) => setIkinciSayfa(e.target.value)} />
                  </label>
                  {ikinciEngeli && <p className="mt-1 text-sm text-bad">{ikinciEngeli}</p>}
                  <ul className="mt-1.5 list-disc pl-5 text-xs text-ink-2">
                    <li>{cevir("Filed to {0} › {1}. After filing, “Also file as {2}” files the same PDF with pages {3} for {2}. You confirm the pages there.", ikinci.rn ?? "", ikinciAd, ikinciAd, ikinciSayfa || "—")}</li>
                    <li>{cevir("Nothing is filed for another riser. The MRB does not change; this file is not sent to the MRB converter.")}</li>
                  </ul>
                  <p className="mt-1 text-xs text-ink-3">{cevir("Done once through today’s Project documents link; the card then shows who filed it and when.")}</p>
                  <Buton
                    kucuk tur="birincil" className="mt-1.5" disabled={mesgul !== null || !ikinciAralik || Boolean(ikinciEngeli)} mesgul={mesgul === "ikinci"}
                    onClick={() => { if (ikinciAralik) void calistir("ikinci", () => api.post(PROJE_UCU.ikinci(satirId), { sayfaBas: ikinciAralik.bas, sayfaSon: ikinciAralik.son })); }}
                  >
                    {cevir("File as {0} too", ikinciAd)}
                  </Buton>
                </>
              )}
            </div>
          )}

          {okunmayanlarMetni(o.tanima) && <p className="mt-1 text-xs text-ink-3">{okunmayanlarMetni(o.tanima)}</p>}
        </>
      )}

      {o && k.karsilastirma && (
        <div data-bolum="karsilastirma">
          <AltBaslik>{cevir("Compared with OASIS")}</AltBaslik>
          {k.karsilastirma.ts && <p className="text-xs text-ink-3">{tarihSaat(k.karsilastirma.ts)}{k.kod && <> · {cevir("Reading job {0}", k.kod)}</>}</p>}
          {karsilastirmaSatirlari.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-sm">
              {karsilastirmaSatirlari.map((n, i) => (
                <li key={i} className="flex gap-2">
                  <Nokta k={n.karsilastirildi ? "kesin" : n.suphe ? "suphe" : "bos"} />
                  <span className="min-w-0">{n.metin}</span>
                </li>
              ))}
            </ul>
          )}
          {!k.karsilastirma.uyumlu && !salt && <p className="mt-1 text-xs text-ink-3">{cevir("Change the type and OASIS compares the same reading again; it is not read again.")}</p>}
          <AltBaslik>{cevir("Alerts from the reading")}</AltBaslik>
          {!k.karsilastirma.karsilastirildi && tumUyarilar.length === 0
            ? <Band tip="not">{salt ? cevir("This reading was not compared with OASIS.") : cevir("This reading was not compared with OASIS. Use Compare again to compare it.")}</Band>
            : tumUyarilar.length === 0
              ? <p className="text-sm text-ink-2">{cevir("No alerts from the comparison.")}</p>
              : (
                <ul className="space-y-1.5">
                  {tumUyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                </ul>
              )}
          {k.karsilastirma.bulgular.length > 0 && (
            <>
              <AltBaslik>{cevir("Notes from the reading")}</AltBaslik>
              <ul className="space-y-0.5 text-xs text-ink-3">
                {k.karsilastirma.bulgular.map((b, i) => <li key={i}><span className="mr-1.5 font-mono">{b.kod}</span>{b.mesaj}</li>)}
              </ul>
            </>
          )}
          {degismedi && <p className="mt-1.5 text-sm text-ink-2">{cevir("No change: the comparison gives the same alerts.")}</p>}
          <p className="mt-2 flex flex-wrap gap-3 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1"><Nokta k="kesin" />{cevir("certain")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="suphe" />{cevir("check")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="bos" />{cevir("not read")}</span>
          </p>
        </div>
      )}

      {(ey.gonder || ey.cikar || ey.girdi || ey.karsilastir) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {ey.gonder && (
            <Buton kucuk tur="birincil" disabled={mesgul !== null} mesgul={mesgul === "ekle"} onClick={() => calistir("ekle", () => api.post(PROJE_UCU.sira(satirId), { islem: "ekle" }))}>
              {cevir(okumaDugmesi(k))}
            </Buton>
          )}
          {ey.karsilastir && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "karsilastir"} onClick={() => calistir("karsilastir", async () => {
              const r = await api.post<{ ok: true; degisti: boolean }>(OKUMA_UCU.karsilastir(satirId));
              if (!r.degisti) setDegismedi(true);
            })}>
              {cevir("Compare again")}
            </Buton>
          )}
          {ey.cikar && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "cikar"} onClick={() => calistir("cikar", () => api.post(PROJE_UCU.sira(satirId), { islem: "cikar" }))}>
              {cevir("Remove from reading queue")}
            </Buton>
          )}
          {ey.girdi && (
            <>
              <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "girdi"} onClick={() => secici.current?.click()}>{cevir("Upload the reading excel by hand")}</Buton>
              <input
                ref={secici}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void calistir("girdi", () => dosyaYukle(PROJE_UCU.girdi(satirId), f, {}, {}, "excel"));
                }}
              />
            </>
          )}
        </div>
      )}
      {hata && <Band tip="duvar" className="mt-2">{hata}</Band>}
    </div>
  );
}
